import React from 'react';
import { Typography, TTypographyProps } from '@rbx/ui';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import useMarkdownPStyles from './MarkdownP.styles';

type AliasComponentOptions = {
  paragraphProps?: TTypographyProps;
};

const Paragraph = (props: TTypographyProps) => {
  const { enableEngineReferenceRevamp } = useAppConfig();
  const {
    classes: { root },
  } = useMarkdownPStyles({ enableEngineReferenceRevamp: !!enableEngineReferenceRevamp });
  return <Typography paragraph component='p' className={root} {...props} />;
};

const isInlineChild = (child: React.ReactNode): boolean => {
  if (
    child &&
    typeof child === 'object' &&
    'props' in child &&
    (child as React.ReactElement).props.paragraph !== undefined
  ) {
    return false;
  }
  return true;
};

function MarkdownP(options?: AliasComponentOptions) {
  return function MarkdownPCallback(props: TTypographyProps) {
    // note: next 14 is sensitive to children being included in opening tag.
    // children should be passed as the contents between the opening and closing tags
    const { children, ...otherProps } = props;
    const childrenList = React.Children.toArray(children);
    const nonInlineChildren = childrenList.filter((child) => typeof child !== 'string');
    if (nonInlineChildren.length > 0) {
      // spaces were not properly used to separate the text from e.g. headers
      // -> warn and attempt to fix invalid HTML
      if (process.env.environment === 'development')
        // eslint-disable-next-line no-console
        console.warn(
          `Invalid Markdown (HTML is not semantic): non-inline elements detected in a paragraph. Add lines between paragraph text and headers, block components, etc. Affected nodes:`,
          children,
        );
      const setsByType = childrenList.reduce(
        (
          accum: Array<{
            type: 'inline' | 'block';
            children: React.ReactNode[];
          }>,
          child: (typeof childrenList)[0],
        ) => {
          const type = isInlineChild(child) ? 'inline' : 'block';
          if (accum[accum.length - 1]?.type === type) {
            // add to previous set
            accum[accum.length - 1].children.push(child);
          } else {
            // push new set
            accum.push({
              type,
              children: [child],
            });
          }
          return accum;
        },
        [],
      );
      return (
        <React.Fragment>
          {setsByType.map((childrenSet) => {
            return childrenSet.type === 'inline' ? (
              <Paragraph
                {...(options?.paragraphProps ? options.paragraphProps : {})}
                {...otherProps}>
                {childrenSet.children}
              </Paragraph>
            ) : (
              childrenSet.children
            );
          })}
        </React.Fragment>
      );
    }

    return (
      <Paragraph {...(options?.paragraphProps ? options.paragraphProps : {})} {...otherProps}>
        {children}
      </Paragraph>
    ) as unknown as HTMLParagraphElement;
  };
}

MarkdownP.displayName = 'MarkdownP';

export default MarkdownP;
